import React from "react";
import { Link } from "react-router-dom";

// Backend returns strict-taxonomy errors shaped as:
//   "Unknown taxonomy — categories: Luxury, Premium; brands: Diorr; sub_categories: Men.
//    Create them in the admin dashboard before re-importing."
// Some flows wrap that in a "<identifier> – <error>" prefix (en-dash separator).
// These helpers parse the string into structured groups and render it with
// deep-links to each taxonomy admin page so the user doesn't have to go hunt.

// Route map — keys are the group names the backend emits (plural and singular
// tolerated). Route paths mirror src/Components/AppRoutes/AppRoutes.jsx.
export const TAXONOMY_GROUPS = {
  categories:     { label: "Categories",     route: "/category" },
  category:       { label: "Categories",     route: "/category" },
  sub_categories: { label: "Sub-categories", route: "/subcategory" },
  sub_category:   { label: "Sub-categories", route: "/subcategory" },
  brands:         { label: "Brands",         route: "/brand" },
  brand:          { label: "Brands",         route: "/brand" },
  types:          { label: "Types",          route: "/type" },
  type:           { label: "Types",          route: "/type" },
  occasions:      { label: "Occasions",      route: "/occassion" },
  occasion:       { label: "Occasions",      route: "/occassion" },
  colors:         { label: "Colors",         route: "/color" },
  color:          { label: "Colors",         route: "/color" },
  sizes:          { label: "Sizes",          route: "/size" },
  size:           { label: "Sizes",          route: "/size" },
};

// "Unknown taxonomy —" — em-dash, en-dash and hyphen all accepted so the UI
// doesn't break if the backend normalizes the separator later.
const TAXONOMY_PREFIX_RE = /Unknown taxonomy\s*[—–-]\s*/i;

export function parseTaxonomyError(rawMessage) {
  const raw = String(rawMessage || "").trim();
  if (!raw) return { raw, isTaxonomy: false, groups: [], trailing: "" };

  const prefixMatch = raw.match(TAXONOMY_PREFIX_RE);
  if (!prefixMatch) {
    return { raw, isTaxonomy: false, groups: [], trailing: raw };
  }

  const afterPrefix = raw.slice(prefixMatch.index + prefixMatch[0].length);
  // Cut at ". Create them in ..." so we don't list the help sentence as a group.
  const createIdx = afterPrefix.search(/\.\s*Create them in/i);
  const groupsPart = createIdx >= 0 ? afterPrefix.slice(0, createIdx) : afterPrefix;

  const groups = groupsPart
    .split(";")
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const colonIdx = chunk.indexOf(":");
      if (colonIdx < 0) return null;
      const key = chunk.slice(0, colonIdx).trim().toLowerCase().replace(/\s+/g, "_");
      const itemsPart = chunk.slice(colonIdx + 1).trim();
      const items = itemsPart.split(",").map((s) => s.trim()).filter(Boolean);
      if (items.length === 0) return null;
      const meta = TAXONOMY_GROUPS[key] || { label: key, route: null };
      return { key, label: meta.label, route: meta.route, items };
    })
    .filter(Boolean);

  return { raw, isTaxonomy: groups.length > 0, groups };
}

// Normalizes a raw `failed` entry into {identifier, row, message}.
// - bulk-insert returns strings:       "Name – Unknown taxonomy — brands: Diorr..."
// - bulk-update returns objects:       { row: 7, identifier: "Name", error: "..." }
// Only en-dash (" – ") is treated as the identifier/error separator; em-dash
// appears inside error bodies (e.g. "Unknown taxonomy — brands: ...") and must
// NOT be split on, or we'd strip the "Unknown taxonomy" prefix as a fake name.
// Normalizes a server failure entry into one shape the UI can match on.
//
// `id` and `barcode` are what callers should match rows by. `identifier` is
// for DISPLAY only — never match on it. Product names are optional in staging
// (barcode is the only required field), so a name-based match resolves to ""
// for most failures and silently lands on the first blank-name row, putting
// the error badge on an innocent product.
//
// The legacy "<identifier> – <message>" string form is still parsed: older
// responses and any not-yet-migrated endpoint use it. Those carry no id, so
// callers fall back to identifier matching for them.
export function normalizeFailedEntry(entry) {
  const empty = { id: null, barcode: null, identifier: "", row: null, message: "" };
  if (entry == null) return empty;

  if (typeof entry === "string") {
    const splitIdx = entry.indexOf(" – ");
    if (splitIdx >= 0) {
      return {
        ...empty,
        identifier: entry.slice(0, splitIdx).trim(),
        message: entry.slice(splitIdx + 3).trim(),
      };
    }
    return { ...empty, message: entry.trim() };
  }

  return {
    id: entry.id ?? null,
    barcode: entry.barcode ?? null,
    identifier: entry.identifier || entry.barcode || entry.name || "",
    row: entry.row ?? null,
    message: entry.error || entry.message || JSON.stringify(entry),
  };
}

// Find the grid row a failure entry refers to.
//
// Order matters: `id` is exact and always correct; `barcode` is unique and
// present on every staged row; `identifier` is a last-resort match for legacy
// string entries. A blank identifier never matches — returning undefined is
// correct, because flagging an arbitrary row is worse than flagging none.
export function findRowForFailure(rows, entry) {
  if (!entry || !Array.isArray(rows)) return undefined;
  if (entry.id != null) {
    const byId = rows.find((r) => r.id === entry.id);
    if (byId) return byId;
  }
  if (entry.barcode) {
    const byBarcode = rows.find((r) => r.barcode === entry.barcode);
    if (byBarcode) return byBarcode;
  }
  if (entry.identifier) {
    return rows.find((r) => r.name === entry.identifier);
  }
  return undefined;
}

export function TaxonomyErrorReport({ errors, title, subtitle, onClose }) {
  if (!errors || errors.length === 0) return null;
  const normalized = errors.map(normalizeFailedEntry);
  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
              {title || `${normalized.length} row(s) failed`}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted small mb-3">
              {subtitle ||
                "Taxonomy values referenced by these rows don't exist yet. Create them in the admin dashboard, then re-import."}
            </p>
            <ul className="list-unstyled mb-0">
              {normalized.map((err, idx) => {
                const parsed = parseTaxonomyError(err.message);
                const heading = [
                  err.row != null ? `Row ${err.row}` : null,
                  err.identifier || null,
                ]
                  .filter(Boolean)
                  .join(" — ");
                return (
                  <li key={idx} className="border rounded p-3 mb-2 bg-light">
                    {heading && (
                      <div className="fw-semibold mb-2">{heading}</div>
                    )}
                    {parsed.isTaxonomy ? (
                      <>
                        <div className="text-muted small mb-2">
                          Missing taxonomy values:
                        </div>
                        {parsed.groups.map((g) => (
                          <div key={g.key} className="mb-1 d-flex flex-wrap align-items-baseline">
                            <span className="fw-semibold me-2">{g.label}:</span>
                            <span className="me-2 text-break">
                              {g.items.join(", ")}
                            </span>
                            {g.route && (
                              <Link
                                to={g.route}
                                target="_blank"
                                rel="noreferrer"
                                className="small"
                              >
                                Open {g.label} <i className="bi bi-box-arrow-up-right ms-1"></i>
                              </Link>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <div
                        className="small text-break"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {err.message}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TaxonomyPreflightHint() {
  return (
    <div className="alert alert-info d-flex align-items-start mb-3" role="alert">
      <i
        className="bi bi-info-circle-fill me-2 mt-1"
        style={{ fontSize: 18 }}
      ></i>
      <div>
        <strong>Before uploading:</strong> Only <strong>barcode</strong> is
        required. Categories, sub-categories, brands and types that don't exist
        yet are created for you when you publish — a row is never rejected for
        naming something new. Anything left blank can be filled in from the
        pending-products grid before you publish.
        <div className="mt-1 small">
          Manage taxonomy directly:{" "}
          <Link to="/category" target="_blank" rel="noreferrer">Categories</Link>
          {" · "}
          <Link to="/subcategory" target="_blank" rel="noreferrer">Sub-categories</Link>
          {" · "}
          <Link to="/brand" target="_blank" rel="noreferrer">Brands</Link>
          {" · "}
          <Link to="/type" target="_blank" rel="noreferrer">Types</Link>
          {" · "}
          <Link to="/occassion" target="_blank" rel="noreferrer">Occasions</Link>
          {" · "}
          <Link to="/color" target="_blank" rel="noreferrer">Colors</Link>
          {" · "}
          <Link to="/size" target="_blank" rel="noreferrer">Sizes</Link>
        </div>
      </div>
    </div>
  );
}
